import React from 'react';
import { motion } from 'framer-motion';
import { Bell, Send, Search, Info, CheckCircle, AlertTriangle, PartyPopper, User, Users as UsersIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';

const BroadcastSection = ({
    newNotification,
    setNewNotification,
    sendNotification,
    notifications,
    setNotifications,
    notificationRecipient,
    setNotificationRecipient,
    userSearchQuery,
    setUserSearchQuery,
    selectedRecipientUid,
    setSelectedRecipientUid,
    users,
    deleteDoc,
    doc,
    db,
    toast,
    isMobile
}) => {
    const filteredUsers = users.filter(u =>
        (u.displayName || '').toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(userSearchQuery.toLowerCase())
    );

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <Card className="p-8 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem] shadow-2xl overflow-hidden relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />

                <div className="relative z-10 space-y-8">
                    <div>
                        <h3 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-2">
                            <Bell className="text-primary" /> Send Broadcast Notification
                        </h3>
                        <p className="text-muted-foreground text-sm font-bold opacity-70">Deploy push notifications to all or specific users</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                    Notification Title
                                </label>
                                <Input
                                    placeholder="Announcement Title"
                                    value={newNotification.title}
                                    onChange={e => setNewNotification({ ...newNotification, title: e.target.value })}
                                    className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight h-[50px] text-foreground"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                    Message Body
                                </label>
                                <Textarea
                                    placeholder="Type your message here..."
                                    value={newNotification.message}
                                    onChange={e => setNewNotification({ ...newNotification, message: e.target.value })}
                                    className="rounded-2xl min-h-[120px] bg-background/50 border-border/60 font-bold tracking-tight text-foreground"
                                />
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                        Category
                                    </label>
                                    <select
                                        className="w-full rounded-2xl p-3 bg-background/50 border border-border/60 font-bold text-sm outline-none focus:ring-2 focus:ring-primary/20 appearance-none h-[50px] text-foreground"
                                        value={newNotification.type}
                                        onChange={e => setNewNotification({ ...newNotification, type: e.target.value })}
                                    >
                                        <option value="info">ℹ️ System Info</option>
                                        <option value="success">✅ Update/Success</option>
                                        <option value="warning">⚠️ Alert/Warning</option>
                                        <option value="event">🎉 Special Event</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                        Recipient
                                    </label>
                                    <select
                                        className="w-full rounded-2xl p-3 bg-background/50 border border-border/60 font-bold text-sm outline-none focus:ring-2 focus:ring-primary/20 appearance-none h-[50px] text-foreground"
                                        value={notificationRecipient}
                                        onChange={e => setNotificationRecipient(e.target.value)}
                                    >
                                        <option value="all">📢 All Users</option>
                                        <option value="specific">👤 Specific User</option>
                                    </select>
                                </div>
                            </div>

                            {notificationRecipient === 'specific' && (
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-4 border-t border-border/40">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                                        <Input
                                            placeholder="Search users..."
                                            value={userSearchQuery}
                                            onChange={e => setUserSearchQuery(e.target.value)}
                                            className="pl-10 rounded-xl bg-background border-border/40 font-bold text-sm h-[40px]"
                                        />
                                    </div>
                                    <select
                                        className="w-full rounded-xl p-3 bg-background border border-border/40 font-bold text-sm outline-none h-[50px]"
                                        value={selectedRecipientUid}
                                        onChange={e => setSelectedRecipientUid(e.target.value)}
                                    >
                                        <option value="">-- Select Recipient ({filteredUsers.length} found) --</option>
                                        {filteredUsers.map(u => (
                                            <option key={u.id} value={u.id}>{u.displayName || 'Anonymous'} ({u.email})</option>
                                        ))}
                                    </select>
                                </motion.div>
                            )}

                            <div className="pt-4">
                                <Button
                                    onClick={sendNotification}
                                    className="w-full py-7 rounded-2xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-xs shadow-xl shadow-primary/30 flex items-center justify-center gap-3 transition-all hover:scale-[1.02]"
                                >
                                    <Send size={18} /> Deploy Notification
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="space-y-4">
                <h3 className="text-xl font-black text-foreground tracking-tight flex items-center gap-2 px-4">
                    <Info size={18} className="text-primary" /> Delivery History
                </h3>

                <div className="grid grid-cols-1 gap-3">
                    {notifications.map((n, i) => (
                        <motion.div
                            key={n.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                        >
                            <Card className="p-4 bg-card/40 backdrop-blur-xl border-border/40 rounded-2xl flex items-center justify-between gap-4 group">
                                <div className="flex items-center gap-4 flex-1">
                                    <div className="w-12 h-12 rounded-xl bg-background/50 border border-border/60 flex items-center justify-center text-xl shadow-inner group-hover:scale-110 transition-transform">
                                        {n.type === 'success' ? '✅' : n.type === 'warning' ? '⚠️' : n.type === 'event' ? '🎉' : 'ℹ️'}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-foreground text-sm group-hover:text-primary transition-colors">{n.title}</h4>
                                        <p className="text-muted-foreground text-xs font-semibold opacity-70 line-clamp-1">{n.message}</p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        try {
                                            await deleteDoc(doc(db, 'notifications', n.id));
                                            setNotifications(prev => prev.filter(x => x.id !== n.id));
                                            toast.success('Deleted');
                                        } catch (err) {
                                            toast.error('Failed to delete');
                                        }
                                    }}
                                    className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all opacity-0 group-hover:opacity-100"
                                >
                                    <Bell size={18} className="rotate-12" />
                                </Button>
                            </Card>
                        </motion.div>
                    ))}

                    {notifications.length === 0 && (
                        <div className="text-center py-20 bg-card/40 backdrop-blur-xl rounded-[2.5rem] border-2 border-dashed border-border/60 opacity-40">
                            <Bell size={48} className="mx-auto mb-4" />
                            <div className="font-black uppercase tracking-widest text-xs">No broadcast history</div>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default BroadcastSection;
